import Icon from 'components/atoms/Icon';
import { useSupportEmail } from '@/contexts/SupportEmail/SupportEmailContext';
import { StageCandidateActionType } from 'enums/stageAction';
import {
  BEHAVIOURAL_TEST_SHORT_LABEL,
  shouldUseBehaviouralLabel,
} from 'helpers/behaviouralRelabel';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Link,
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import {
  isUntimedTest,
  LabelsType,
  mapTestToIcon,
  mapTestTypeToLabel,
  steps,
  TAdjustmentsFlowRoutes,
} from '../AccessibilityAdjustments.helpers';
import SupportEmailDisclaimer from '../SupportEmailDisclaimer';
import styles from './AccessibilityDetails.module.scss';

const AccessibilityDetails = ({
  tests,
  extraTime,
  aiInterviewExtraTime,
  shouldContactUs,
  handleProgress,
  flowRoutes,
  usesBehaviouralLabel,
  extraTimeOverrides,
  isLiveChatEnabled = true,
}: {
  tests: StageCandidateActionType[];
  extraTime: string;
  /** The interview's own entitlement, which differs from the written tests'. */
  aiInterviewExtraTime: string | null;
  /**
   * Whether this candidate declared a difficulty the extra time alone may not
   * settle — hearing the interviewer, or speaking — in which case we also
   * offer to arrange something individually.
   */
  shouldContactUs: boolean;
  handleProgress: (step: steps) => void;
  flowRoutes: TAdjustmentsFlowRoutes;
  usesBehaviouralLabel?: boolean;
  extraTimeOverrides?: Partial<Record<StageCandidateActionType, string>>;
  /**
   * The company's "Display live chat to users" setting. When off, the copy
   * points candidates to email only.
   */
  isLiveChatEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { supportEmail, supportEmailHref } = useSupportEmail();
  const navigate = useNavigate();

  useEffect(() => {
    // Set completedStep to 'details' in order to block backwards navigation with the
    // browser's back button
    handleProgress('details');
  }, [handleProgress]);

  const handleSubmit = () => {
    navigate(flowRoutes.exitUrl);
  };

  return (
    <section className={styles.container} data-testid="accessibility-details">
      <p className={styles.description}>
        <Trans
          i18nKey="accessibilityAdjustments.details.description"
          values={{
            extraTime: t('accessibilityAdjustments.details.extraTime'),
          }}
          components={{ strong: <span /> }}
        />
      </p>
      <div className={styles.testsContainer}>
        {tests.map(test => {
          const isAiInterviewAction =
            test === StageCandidateActionType.AiInterviews;
          // The interview earns its own amount, and may earn none at all — an
          // assistive-technology answer changes nothing about a spoken call.
          if (isAiInterviewAction && !aiInterviewExtraTime) {
            return null;
          }
          return (
            <TestItem
              key={test}
              testType={test}
              extraTime={
                isAiInterviewAction
                  ? (aiInterviewExtraTime as string)
                  : (extraTimeOverrides?.[test] ?? extraTime)
              }
              usesBehaviouralLabel={usesBehaviouralLabel}
            />
          );
        })}
      </div>
      {shouldContactUs && (
        <div
          className={styles.contactUsNotice}
          data-testid="accessibility-details__contact-us"
        >
          <p className={styles.contactUsTitle}>
            {t('accessibilityAdjustments.details.aiInterview.contactUs.title')}
          </p>
          <p className={styles.contactUsBody}>
            {t('accessibilityAdjustments.details.aiInterview.contactUs.body')}
          </p>
          <p className={styles.contactUsBody}>
            {t(
              'accessibilityAdjustments.details.aiInterview.contactUs.practice'
            )}
          </p>
        </div>
      )}
      {/* The notice above already says to get in touch, so these candidates go
          straight from it to the address rather than reading it twice. */}
      {shouldContactUs ? (
        <Link
          to={supportEmailHref}
          target="_blank"
          aria-label={t('common.sendEmailTo', { email: supportEmail })}
          data-testid="accessibility-details__support-email-link"
        >
          {supportEmail}
        </Link>
      ) : (
        <SupportEmailDisclaimer
          i18nPrefix="accessibilityAdjustments.details"
          isLiveChatEnabled={isLiveChatEnabled}
          testIdPrefix="accessibility-details"
          className={styles.disclaimer}
        />
      )}
      <Button
        onClick={handleSubmit}
        type="submit"
        aria-label={t('common.continue')}
        data-testid="accessibility-summary__submit-button"
        className={styles.cta}
      >
        {t('common.continue')}
      </Button>
    </section>
  );
};

export default AccessibilityDetails;

const TestItem = ({
  testType,
  extraTime,
  usesBehaviouralLabel,
}: {
  testType: StageCandidateActionType;
  extraTime: string;
  // TEMP HACK — "Personality" → "Behavioural" relabel (per-client, temporary).
  // See helpers/behaviouralRelabel.ts
  usesBehaviouralLabel?: boolean;
}) => {
  const { t } = useTranslation();
  const label = mapTestTypeToLabel[testType as LabelsType];
  const icon = mapTestToIcon[testType as LabelsType];

  return (
    <div className={styles.testItemContainer}>
      <div className={styles.iconContainer}>
        <Icon icon={icon} />
      </div>
      <div className={styles.rightSection}>
        <span className={styles.testType}>
          {/* TEMP HACK — "Personality" → "Behavioural" relabel (per-client,
              temporary). See helpers/behaviouralRelabel.ts */}
          {label === 'personality' &&
          shouldUseBehaviouralLabel(usesBehaviouralLabel)
            ? BEHAVIOURAL_TEST_SHORT_LABEL
            : t(`common.testTypes.${label}`)}
        </span>
        <Tag
          isDismissible={false}
          isPresentational={true}
          tagColor={isUntimedTest(testType) ? TagColor.DISABLED : TagColor.BLUE}
          containerClass={styles.timeLabel}
          isDisabled={isUntimedTest(testType)}
          size={TagSize.SMALL}
        >
          {isUntimedTest(testType)
            ? t('accessibilityAdjustments.details.untimed')
            : extraTime +
              ' ' +
              t('accessibilityAdjustments.details.extraTimeTag')}
        </Tag>
      </div>
    </div>
  );
};
